import type {
  Budget,
  Expense,
  CurrencyRate,
  BudgetSummary,
  ExpenseReport,
  GroupExpenseBalance,
  DebtSettlement
} from './budget.types.js'
import { db } from '../../core/database/firebase.js'
import { FieldValue } from 'firebase-admin/firestore'
import { createLogger } from '../../common/utils/logger.js'
import { safeJsonParse } from '../../utils/sanitize.js'
import type { FirestoreDoc } from '../../common/types/firestore.js'
import { toDateOrNow } from '../../common/utils/normalize.js'
import { CACHE_TTL } from '../../common/constants.js'

const logger = createLogger('BudgetService')
import { redis } from '../../core/database/redis.js'

const FIXER_BASE_URL = 'https://api.exchangerate.host'

export class BudgetService {
  /**
   * Create budget for a plan
   */
  async createBudget(planId: string, userId: string, data: {
    totalBudget: number
    currency: string
    categories: Budget['categories']
  }): Promise<Budget> {
    try {
      const budget: Omit<Budget, 'id'> = {
        planId,
        userId,
        totalBudget: data.totalBudget,
        currency: data.currency,
        categories: data.categories,
        spent: 0,
        remaining: data.totalBudget,
        createdAt: new Date(),
        updatedAt: new Date()
      }

      const ref = await db.collection('budgets').add(budget)
      
      return {
        id: ref.id,
        ...budget
      }
    } catch (error) {
      logger.error('Failed to create budget', { error, planId })
      throw error
    }
  }

  /**
   * Add expense to budget
   */
  async addExpense(budgetId: string, data: Omit<Expense, 'id' | 'convertedAmount' | 'createdAt' | 'updatedAt'>): Promise<Expense> {
    try {
      // Get budget to check currency
      const budgetDoc = await db.collection('budgets').doc(budgetId).get()
      
      if (!budgetDoc.exists) {
        throw new Error('Budget not found')
      }

      const budget = { id: budgetDoc.id, ...budgetDoc.data() } as Budget

      // Convert currency if different
      let convertedAmount = data.amount
      if (data.currency !== budget.currency) {
        convertedAmount = await this.convertCurrency(
          data.amount,
          data.currency,
          budget.currency
        )
      }

      const expense: Omit<Expense, 'id'> = {
        ...data,
        budgetId,
        convertedAmount,
        createdAt: new Date(),
        updatedAt: new Date()
      }

      const ref = await db.collection('expenses').add(expense)

      // 🔒 CRITICAL FIX: Use FieldValue.increment() to prevent race conditions
      // Two simultaneous expenses would overwrite each other with read-modify-write
      const updateData: Record<string, any> = {
        spent: FieldValue.increment(convertedAmount),
        remaining: FieldValue.increment(-convertedAmount),
        updatedAt: new Date()
      }

      // Update category spent atomically
      const category = budget.categories.find(c => c.id === data.categoryId)
      if (category) {
        const categoryIndex = budget.categories.findIndex(c => c.id === data.categoryId)
        updateData[`categories.${categoryIndex}.spent`] = FieldValue.increment(convertedAmount)
      }

      await db.collection('budgets').doc(budgetId).update(updateData)

      return {
        id: ref.id,
        ...expense
      }
    } catch (error) {
      logger.error('Failed to add expense', { error, budgetId })
      throw error
    }
  }

  /**
   * Get budget summary
   */
  async getBudgetSummary(budgetId: string): Promise<BudgetSummary> {
    try {
      const budgetDoc = await db.collection('budgets').doc(budgetId).get()
      
      if (!budgetDoc.exists) {
        throw new Error('Budget not found')
      }

      const budget = { id: budgetDoc.id, ...budgetDoc.data() } as Budget

      // Get all expenses
      const expensesSnapshot = await db
        .collection('expenses')
        .where('budgetId', '==', budgetId)
        .get()

      const expenses = expensesSnapshot.docs.map((doc: FirestoreDoc) => ({
        id: doc.id,
        ...doc.data()
      })) as Expense[]

      // Calculate daily average
      const firstExpense = [...expenses].sort((a, b) => a.date.getTime() - b.date.getTime())[0]
      const daysSinceStart = firstExpense
        ? Math.ceil((Date.now() - firstExpense.date.getTime()) / (1000 * 60 * 60 * 24))
        : 1
      const dailyAverage = budget.spent / daysSinceStart

      // Project total based on plan duration
      const planDoc = await db.collection('plans').doc(budget.planId).get()
      if (!planDoc.exists) {
        throw new Error('Plan not found')
      }
      const plan = planDoc.data()!
      const tripDuration = plan.duration || 7 // default 7 days
      const projectedTotal = dailyAverage * tripDuration

      const categorySummaries = budget.categories.map(cat => ({
        categoryId: cat.id,
        name: cat.name,
        allocated: cat.allocated,
        spent: cat.spent,
        remaining: cat.allocated - cat.spent,
        percentageSpent: cat.allocated > 0 ? (cat.spent / cat.allocated) * 100 : 0,
        transactionCount: expenses.filter(e => e.categoryId === cat.id).length
      }))

      return {
        totalBudget: budget.totalBudget,
        totalSpent: budget.spent,
        remaining: budget.remaining,
        percentageSpent: (budget.spent / budget.totalBudget) * 100,
        dailyAverage,
        projectedTotal,
        overBudget: budget.spent > budget.totalBudget,
        categories: categorySummaries
      }
    } catch (error) {
      logger.error('Failed to get budget summary', { error, budgetId })
      throw error
    }
  }

  /**
   * Generate expense report
   */
  async generateExpenseReport(budgetId: string, startDate: Date, endDate: Date): Promise<ExpenseReport> {
    try {
      const summary = await this.getBudgetSummary(budgetId)

      const expensesSnapshot = await db
        .collection('expenses')
        .where('budgetId', '==', budgetId)
        .where('date', '>=', startDate)
        .where('date', '<=', endDate)
        .orderBy('date', 'desc')
        .get()

      const expenses = expensesSnapshot.docs.map((doc: FirestoreDoc) => ({
        id: doc.id,
        ...doc.data()
      })) as Expense[]

      // Group by category
      const byCategory: Record<string, number> = {}
      for (const expense of expenses) {
        byCategory[expense.categoryId] = (byCategory[expense.categoryId] || 0) + (expense.convertedAmount || expense.amount)
      }

      // Group by user
      const byUser: Record<string, number> = {}
      for (const expense of expenses) {
        byUser[expense.userId] = (byUser[expense.userId] || 0) + (expense.convertedAmount || expense.amount)
      }

      // Group by date
      const byDate: Record<string, number> = {}
      for (const expense of expenses) {
        const dateKey = expense.date.toISOString().split('T')[0]
        byDate[dateKey] = (byDate[dateKey] || 0) + (expense.convertedAmount || expense.amount)
      }

      // Top expenses
      const topExpenses = [...expenses]
        .slice().sort((a, b) => (b.convertedAmount || b.amount) - (a.convertedAmount || a.amount))
        .slice(0, 10)

      const days = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
      const averageDaily = summary.totalSpent / days

      return {
        budgetId,
        period: { start: startDate, end: endDate },
        summary,
        expenses,
        byCategory,
        byUser,
        byDate,
        topExpenses,
        averageDaily,
        projections: {
          endOfTrip: summary.projectedTotal,
          overBudgetBy: summary.overBudget ? summary.totalSpent - summary.totalBudget : undefined
        }
      }
    } catch (error) {
      logger.error('Failed to generate expense report', { error, budgetId })
      throw error
    }
  }

  /**
   * Calculate group expense balances
   */
  async calculateGroupBalance(budgetId: string): Promise<GroupExpenseBalance[]> {
    try {
      const expensesSnapshot = await db
        .collection('expenses')
        .where('budgetId', '==', budgetId)
        .get()

      const expenses = expensesSnapshot.docs.map((doc: FirestoreDoc) => ({
        id: doc.id,
        ...doc.data()
      })) as Expense[]

      // Calculate what each user paid and owes
      const userBalances = new Map<string, { paid: number; owed: number; name: string }>()

      for (const expense of expenses) {
        for (const split of expense.splits) {
          if (!userBalances.has(split.userId)) {
            userBalances.set(split.userId, { paid: 0, owed: 0, name: split.userName })
          }

          const balance = userBalances.get(split.userId)!
          balance.owed += split.amount

          // If this user paid for the expense
          if (expense.userId === split.userId) {
            balance.paid += expense.convertedAmount || expense.amount
          }
        }
      }

      // Calculate net balances and debts
      const balances: GroupExpenseBalance[] = []
      const users = Array.from(userBalances.entries())

      for (const [userId, data] of users) {
        const netBalance = data.paid - data.owed
        const debts: DebtSettlement[] = []

        if (netBalance < 0) {
          // This user owes money
          for (const [otherUserId, otherData] of users) {
            if (otherUserId === userId) continue
            const otherBalance = otherData.paid - otherData.owed

            if (otherBalance > 0) {
              const budgetDoc = await db.collection('budgets').doc(budgetId).get()
              const budget = budgetDoc.data() as Budget

              debts.push({
                from: userId,
                to: otherUserId,
                amount: Math.abs(netBalance),
                currency: budget.currency,
                status: 'pending'
              })
              break
            }
          }
        }

        balances.push({
          userId,
          userName: data.name,
          totalPaid: data.paid,
          totalOwed: data.owed,
          balance: netBalance,
          debts
        })
      }

      return balances
    } catch (error) {
      logger.error('Failed to calculate group balance', { error, budgetId })
      throw error
    }
  }

  /**
   * Convert currency using Exchange Rate API
   */
  async convertCurrency(amount: number, from: string, to: string): Promise<number> {
    if (from === to) return amount

    try {
      const cacheKey = `currency:${from}:${to}`
      const cached = await redis.get(cacheKey)

      let rate: number | undefined

      if (cached) {
        const parsedRate = Number.parseFloat(cached)
        if (Number.isFinite(parsedRate)) {
          rate = parsedRate
        } else {
          logger.warn('Invalid cached exchange rate, refetching', { from, to, cached })
          await redis.del(cacheKey)
        }
      }
      
      if (!rate) {
        // Use exchangerate.host (free, no API key required)
        const url = `${FIXER_BASE_URL}/convert?from=${from}&to=${to}&amount=${amount}`
        const response = await fetch(url)
        const data = await response.json() as { success?: boolean; info?: { rate: number } }

        if (!data.success) {
          throw new Error('Currency conversion failed')
        }

        rate = data.info?.rate ?? 1
        
        // Cache rate
        await redis.setex(cacheKey, CACHE_TTL.CURRENCY_CACHE_SEC, rate.toString())
      }

      return amount * rate
    } catch (error) {
      logger.error('Currency conversion failed', { error, from, to })
      // Return original amount as fallback
      return amount
    }
  }

  /**
   * Get currency rates
   */
  async getCurrencyRates(base: string = 'EUR'): Promise<CurrencyRate> {
    const cacheKey = `currency:rates:${base}`
    const cached = await redis.get(cacheKey)

    if (cached) {
      const parsed = safeJsonParse<CurrencyRate>(cached)
      if (parsed) {
        return parsed
      }
      logger.warn('Failed to parse cached currency rates, fetching fresh', { base })
    }

    try {
      const url = `${FIXER_BASE_URL}/latest?base=${base}`
      const response = await fetch(url)
      const data = await response.json() as { success?: boolean; rates?: Record<string, number> }

      if (!data.success) {
        throw new Error('Failed to fetch currency rates')
      }

      const rates: CurrencyRate = {
        base,
        rates: data.rates ?? {},
        timestamp: new Date()
      }

      await redis.setex(cacheKey, CACHE_TTL.CURRENCY_CACHE_SEC, JSON.stringify(rates))

      return rates
    } catch (error) {
      logger.error('Failed to fetch currency rates', { error, base })
      throw error
    }
  }

  // =================================================================
  // Simple Expense CRUD (for frontend budgetService API compatibility)
  // =================================================================

  /**
   * Get expenses by plan ID (for frontend API)
   */
  async getExpensesByPlanId(planId: string): Promise<SimpleExpense[]> {
    try {
      const snapshot = await db
        .collection('plan_expenses')
        .where('planId', '==', planId)
        .orderBy('createdAt', 'desc')
        .get()

      return snapshot.docs.map((doc: FirestoreDoc) => {
        const data = doc.data()
        return {
          id: doc.id,
          planId: data.planId,
          category: data.category,
          description: data.description,
          amount: data.amount,
          currency: data.currency || 'EUR',
          date: toDateOrNow(data.date),
          paidBy: data.paidBy,
          userId: data.userId,
          splitWith: data.splitWith || [],
          createdAt: toDateOrNow(data.createdAt),
          updatedAt: toDateOrNow(data.updatedAt)
        }
      })
    } catch (error) {
      logger.error('Failed to get expenses by planId', { error, planId })
      return []
    }
  }

  /**
   * Create expense (for frontend API)
   */
  async createExpense(
    planId: string,
    userId: string,
    data: CreateSimpleExpenseDTO
  ): Promise<SimpleExpense> {
    const expense = {
      planId,
      userId,
      category: data.category,
      description: data.description || '',
      amount: data.amount,
      currency: data.currency || 'EUR',
      date: data.date || new Date(),
      paidBy: data.paidBy,
      splitWith: data.splitWith || [],
      createdAt: new Date(),
      updatedAt: new Date()
    }

    const ref = await db.collection('plan_expenses').add(expense)
    logger.info('Expense created', { id: ref.id, planId, category: data.category })

    return {
      id: ref.id,
      ...expense
    }
  }

  /**
   * Update expense (for frontend API)
   */
  async updateExpense(
    expenseId: string,
    userId: string,
    updates: Partial<CreateSimpleExpenseDTO>
  ): Promise<SimpleExpense | null> {
    const docRef = db.collection('plan_expenses').doc(expenseId)
    const doc = await docRef.get()

    if (!doc.exists) {
      return null
    }

    const updateData = {
      ...updates,
      updatedAt: new Date()
    }

    await docRef.update(updateData)
    
    const updated = await docRef.get()
    const data = updated.data()
    
    return {
      id: expenseId,
      planId: data?.planId,
      category: data?.category,
      description: data?.description,
      amount: data?.amount,
      currency: data?.currency || 'EUR',
      date: toDateOrNow(data?.date),
      paidBy: data?.paidBy,
      userId: data?.userId,
      splitWith: data?.splitWith || [],
      createdAt: toDateOrNow(data?.createdAt),
      updatedAt: toDateOrNow(data?.updatedAt)
    }
  }

  /**
   * Delete expense (for frontend API)
   */
  async deleteExpense(
    expenseId: string,
    userId: string
  ): Promise<boolean> {
    const docRef = db.collection('plan_expenses').doc(expenseId)
    const doc = await docRef.get()

    if (!doc.exists) {
      return false
    }

    await docRef.delete()
    logger.info('Expense deleted', { id: expenseId })
    return true
  }

  /**
   * Get expense summary (for frontend API)
   */
  async getExpenseSummary(planId: string): Promise<{
    total: number
    currency: string
    byCategory: Record<string, number>
    byUser: Record<string, number>
    count: number
  }> {
    const expenses = await this.getExpensesByPlanId(planId)
    
    const byCategory: Record<string, number> = {}
    const byUser: Record<string, number> = {}
    let total = 0

    for (const expense of expenses) {
      total += expense.amount
      byCategory[expense.category] = (byCategory[expense.category] || 0) + expense.amount
      byUser[expense.paidBy] = (byUser[expense.paidBy] || 0) + expense.amount
    }

    return {
      total,
      currency: expenses[0]?.currency || 'EUR',
      byCategory,
      byUser,
      count: expenses.length
    }
  }
}

// Types for simple expense API
interface SimpleExpense {
  id: string
  planId: string
  category: string
  description?: string
  amount: number
  currency: string
  date: Date
  paidBy: string
  userId: string
  splitWith: Array<{ userId: string; amount?: number; percentage?: number }>
  createdAt: Date
  updatedAt: Date
}

interface CreateSimpleExpenseDTO {
  category: string
  description?: string
  amount: number
  currency: string
  date: Date
  paidBy: string
  splitWith?: Array<{ userId: string; amount?: number; percentage?: number }>
}

export const budgetService = new BudgetService()
